/**
 * Extracts up to `max` initials (first Unicode letter per word) from a name.
 *
 * Words are split on whitespace. For each word the first Unicode letter
 * (`\p{L}`) is taken — numbers, emoji, symbols, and other non-letter
 * characters are skipped. Supports Latin, CJK, Korean, accented, and
 * other Unicode scripts.
 *
 * @param name - The display name to extract initials from.
 * @param max  - Maximum number of initials to return (default `2`).
 * @returns Uppercase initials string, or `""` if no letters are found.
 *
 * @example
 * getInitials('Carolina Reaper');   // 'CR'
 * getInitials('#123 Restaurant');   // 'R'
 * getInitials('🌮 Taco Shop');      // 'TS'
 * getInitials('日本 Sushi Bar');     // '日S'
 */
const UNICODE_LETTER_RE = /\p{L}/u;

export const getInitials = (name: string, max = 2): string =>
  name
    .split(/\s+/)
    .reduce<string[]>((acc, word) => {
      const ch = word.match(UNICODE_LETTER_RE)?.[0];
      return ch && acc.length < max ? [...acc, ch.toUpperCase()] : acc;
    }, [])
    .join('');
